import { BreadcrumbsProps } from '@factorialco/f0-react/dist/experimental'

import { HandleData } from './useResolvedHandles'

type Breadcrumb = BreadcrumbsProps['breadcrumbs'][number]

// We want to render breadcrumbs as a way to give users a clue about where they are.
// We also want to make sure that we don't show redundant breadcrumbs when we have tabs.
//
// The rationale to render breadcrumbs is as follows:
// * Pages have breadcrumbs up to the point where they have tabs. Then the tabs act as visual clues.
// * Resources have breadcrumbs up to the point where they have tabs and show their
//   ancestors as part of the breadcrumbs, naturally rendering a "subspace" of tabs.
export const getBreadcrumbs = (handles: HandleData[]) => {
  const moduleIndex =
    handles
      .map((resolvedHandle, index) => (resolvedHandle.type === 'module' ? index : undefined))
      .reverse()
      .find((index) => index !== undefined) ?? 0

  const lastResourceIndex = handles
    .map((resolvedHandle, index) => {
      return resolvedHandle.type === 'resource' ? index : undefined
    })
    .reverse()
    .find((index) => index !== undefined)

  const lastResource = (() => {
    const resource = lastResourceIndex !== undefined ? handles[lastResourceIndex] : undefined
    if (resource && resource.type === 'resource') return resource
  })()

  const firstTabsIndex = handles
    .map((resolvedHandle, index) => (resolvedHandle.meta?.subNavigation ? index : undefined))
    .find((index) => index !== undefined)

  const cutoff =
    lastResourceIndex !== undefined || firstTabsIndex !== undefined
      ? Math.max(lastResourceIndex !== undefined ? lastResourceIndex + 1 : 0, firstTabsIndex || 0)
      : undefined

  const breadcrumbs = handles.slice(moduleIndex + 1, cutoff).flatMap<Breadcrumb>((handle) => {
    // Skip breadcrumbs with empty titles (used to conditionally hide breadcrumbs)
    if (handle?.meta?.title === 'show_nothing') {
      return []
    }

    // Show loading state when title is missing (undefined/null)
    if (!handle?.meta?.title) {
      return [
        {
          id: handle.path,
          loading: true,
        },
      ]
    }

    const metaAncestors = 'ancestors' in handle?.meta ? handle.meta.ancestors : []

    const ancestors =
      metaAncestors?.map((ancestor) => ({
        id: ancestor.to,
        label: ancestor.title,
        href: ancestor.to,
      })) || []

    return [
      ...ancestors,
      'siblingsNavigation' in handle?.meta && handle?.meta?.siblingsNavigation?.select
        ? {
            type: 'select',
            id: handle.path,
            searchbox: true,
            options: handle.meta.siblingsNavigation.select.options,
            source: handle.meta.siblingsNavigation.select.source,
            mapOptions:
              'mapOptions' in handle.meta.siblingsNavigation.select
                ? handle.meta.siblingsNavigation.select.mapOptions
                : undefined,
            value: handle.meta.siblingsNavigation.select.value,
            defaultItem: handle.meta.siblingsNavigation.select.defaultItem,
            onChange: handle.meta.siblingsNavigation.select.onChange,
            label: handle.meta.title,
            href: handle.path,
          }
        : {
            id: handle.path,
            label: handle.meta.title,
            href: handle.path,
          },
    ]
  })

  return { breadcrumbs, lastResourceIndex, lastResource, moduleIndex }
}
